import {
  Component,
  ContentChild,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Provider,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {FormGroupDirective, NG_VALUE_ACCESSOR} from '@angular/forms';
import {INgSelectSearchEvent, ItemsSimplePayload, PersistentInputComponent, Position} from '@ideals/types';
import {DropdownPosition, NgSelectComponent} from '@ng-select/ng-select';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';

interface ISelectItem<T> {
  [key: string]: T;
}

type SelectItems<T> = Array<ISelectItem<T>>;

const DEBOUNCE_TIME = 300;

export const SELECT_CONTROL_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => SelectComponent),
  multi: true
};

@Component({
  selector: 'ideals-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [SELECT_CONTROL_VALUE_ACCESSOR],
  encapsulation: ViewEncapsulation.None
})
export class SelectComponent<T> extends PersistentInputComponent implements OnInit, OnDestroy {
  @ViewChild('select')
  public readonly select: NgSelectComponent;

  @ViewChild('headerSearch')
  public readonly headerSearch: ElementRef;

  @ViewChild('footerSearch')
  public readonly footerSearch: ElementRef;

  @ContentChild(TemplateRef)
  public optionPrefixTemplateRef: TemplateRef<any>;

  @Input()
  public items: ItemsSimplePayload = [];

  @Input()
  public translateValues = false;

  @Input()
  public customSearch = false;

  @Input()
  public dropdownPositionBreakpoint = 0;

  @Input()
  public searchPlaceholder = '';

  private readonly _selectChanged = new Subject<any>();
  private readonly _componentDestroyed = new Subject<void>();
  private _searchInProgress: boolean;

  constructor(
    @Optional()
    public readonly parentFormGroup: FormGroupDirective
  ) {
    super(parentFormGroup);
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this._selectChanged
      .pipe(
        takeUntil(this._componentDestroyed),
        debounceTime(DEBOUNCE_TIME),
        distinctUntilChanged(),
      )
      .subscribe((value) => {
        this.doChange(value);
      });
  }

  public ngOnDestroy(): void {
    this._componentDestroyed.next();
    this._componentDestroyed.unsubscribe();
  }

  public get dropdownPosition(): DropdownPosition {
    if (this.dropdownPositionBreakpoint === 0) {
      return 'auto';
    }

    return document.documentElement.clientHeight > this.dropdownPositionBreakpoint ? 'bottom' : 'top';
  }

  public get searchable(): boolean {
    const {multiple, searchable} = this.config;
    const searchDisable = multiple && this.value && this.value.length;

    return searchable && !searchDisable;
  }

  public get withCheckboxes(): boolean {
    const {multiple, forceCheckBoxes} = this.config;

    return multiple || forceCheckBoxes;
  }

  public onSelectChange(value: any): void {
    this._selectChanged.next(value);
  }

  public doOpen(): void {
    if (this.customSearch) {
      setTimeout(() => {
        if (this.select.currentPanelPosition === Position.Top) {
          this.footerSearch.nativeElement.focus();

          return;
        }

        if (this.select.currentPanelPosition === Position.Bottom) {
          this.headerSearch.nativeElement.focus();

          return;
        }
      });
    }
  }

  public doSearch(event: INgSelectSearchEvent<SelectItems<T>>): void {
    this._searchInProgress = !!event.term;
    this.searchForItem.emit(event);
  }

  public doBlur(event: any): void {
    this._searchInProgress = false;
    this.controlBlur.emit(event);
  }

  public clearSelection(clearFn: Function, items: SelectItems<string>): void {
    items.forEach((item) => {
      clearFn(item);
    });
  }
}
